import { useState } from 'react';
import { Box, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import AdminTable from '../../shared/AdminTable';
import AdminStatusBadge from '../../shared/AdminStatusBadge';
import SubscriptionDetailModal from './SubscriptionDetailModal';
import { BillingSubscription, AdminTableColumn, AdminTableAction } from '../../../../types/admin';
import { formatDistanceToNow } from 'date-fns';
import { formatCurrency } from '../../../../utils/formatters';

interface SubscriptionsTableProps {
  subscriptions: BillingSubscription[];
  loading?: boolean;
  onCancelSubscription?: (subscriptionId: string, reason?: string) => Promise<{ success: boolean }>;
}

export default function SubscriptionsTable({
  subscriptions,
  loading,
  onCancelSubscription,
}: SubscriptionsTableProps) {
  const [selectedSubscription, setSelectedSubscription] = useState<BillingSubscription | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const columns: AdminTableColumn<BillingSubscription>[] = [
    {
      key: 'user_name',
      label: 'User',
      sortable: true,
      width: 180,
      render: (row) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>{row.user_name}</Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{row.user_email}</Box>
        </Box>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      sortable: true,
      width: 100,
      render: (row) => <Chip label={row.tier.toUpperCase()} size="small" color="primary" />,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 100,
      render: (row) => {
        const statusMap = {
          active: 'active' as const,
          trial: 'pending' as const,
          past_due: 'flagged' as const,
          canceled: 'suspended' as const,
          expired: 'suspended' as const,
        };
        return <AdminStatusBadge status={statusMap[row.status]} />;
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      width: 120,
      render: (row) => (
        <Box>
          <Box sx={{ fontWeight: 500 }}>{formatCurrency(row.amount)}</Box>
          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>/{row.interval}</Box>
        </Box>
      ),
    },
    {
      key: 'payment_method',
      label: 'Payment',
      sortable: false,
      width: 150,
      render: (row) => (
        <Box>
          <Box sx={{ textTransform: 'capitalize' }}>{row.payment_method_brand || row.payment_method}</Box>
          {row.payment_method_last4 && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>••••{row.payment_method_last4}</Box>
          )}
        </Box>
      ),
    },
    {
      key: 'current_period_end',
      label: 'Next Billing',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.current_period_end), { addSuffix: true }),
    },
    {
      key: 'created_at',
      label: 'Customer Since',
      sortable: true,
      width: 150,
      render: (row) => formatDistanceToNow(new Date(row.created_at), { addSuffix: true }),
    },
  ];

  const actions: AdminTableAction<BillingSubscription>[] = [
    {
      label: 'View Details',
      icon: <VisibilityIcon />,
      onClick: (row) => {
        setSelectedSubscription(row);
        setDetailModalOpen(true);
      },
      color: 'primary',
    },
    {
      label: 'Cancel Subscription',
      icon: <CancelIcon />,
      onClick: async (row) => {
        if (onCancelSubscription) {
          await onCancelSubscription(row.id);
        }
      },
      color: 'error',
      disabled: (row) => row.status === 'canceled' || row.status === 'expired',
    },
  ];

  return (
    <>
      <AdminTable columns={columns} data={subscriptions} actions={actions} isLoading={loading} />

      {selectedSubscription && (
        <SubscriptionDetailModal
          open={detailModalOpen}
          subscription={selectedSubscription}
          onClose={() => setDetailModalOpen(false)}
          onCancelSubscription={onCancelSubscription}
        />
      )}
    </>
  );
}
